import * as React from "react";
import {useRef, useState, useCallback, useEffect} from "react";
import {IFilterOption} from "./useResearchFilters";
import {getTranslation} from "../../utils/translationUtil";

export interface IResearchFilterDropdownProps {
    label: string;
    field: string;
    options: IFilterOption[];
    selectedValues: Set<string>;
    onToggle: (field: string, value: string) => void;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

export const ResearchFilterDropdown: React.FunctionComponent<IResearchFilterDropdownProps> = (props) => {
    const {isOpen, onOpenChange} = props;
    const firstCheckboxRef = useRef<HTMLInputElement>(null);
    const [localSelected, setLocalSelected] = useState<Set<string>>(props.selectedValues);
    const activeCount = localSelected.size;

    useEffect(() => {
        setLocalSelected(props.selectedValues);
    }, [props.selectedValues]);

    const handleToggle = (value: string) => {
        setLocalSelected((prev) => {
            const next = new Set(prev);
            if (next.has(value)) {
                next.delete(value);
            } else {
                next.add(value);
            }
            return next;
        });

        setTimeout(() => {
            props.onToggle(props.field, value);
        }, 10);
    };

    const handleTriggerClick = useCallback(() => {
        onOpenChange(!isOpen);
    }, [isOpen, onOpenChange]);

    useEffect(() => {
        if (isOpen && firstCheckboxRef.current) {
            firstCheckboxRef.current.focus();
        }
    }, [isOpen]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            onOpenChange(false);
        }
    }, [onOpenChange]);

    const dropdownId = `research-filter-dropdown-${props.field}`;

    return (
        <div className="research-filter-dropdown" onKeyDown={handleKeyDown}>
            <button
                type="button"
                className={`research-filter-dropdown__trigger${activeCount > 0 ? " research-filter-dropdown__trigger--active" : ""}`}
                onClick={handleTriggerClick}
                aria-expanded={isOpen}
                aria-controls={dropdownId}
                aria-label={getTranslation("research-page.filter-by", "Filter by {label}", {label: props.label})}
            >
                {props.label}
                {activeCount > 0 && (
                    <span className="research-filter-dropdown__badge">{activeCount}</span>
                )}
                <i className="fa-solid fa-caret-down research-filter-dropdown__arrow" aria-hidden="true" />
            </button>

            {isOpen && (
                <div
                    id={dropdownId}
                    className="research-filter-dropdown__dropdown"
                    role="group"
                    aria-label={getTranslation("research-page.filter-options", "{label} filter options", {label: props.label})}
                >
                    {props.options.map((option, idx) => {
                        const isChecked = localSelected.has(option.value);
                        const isZero = option.count === 0;
                        const optionId = `${dropdownId}-option-${idx}`;
                        return (
                            <label
                                key={option.value}
                                className={`research-filter-dropdown__option${isZero ? " research-filter-dropdown__option--zero-count" : ""}`}
                                htmlFor={optionId}
                            >
                                <input
                                    ref={idx === 0 ? firstCheckboxRef : undefined}
                                    id={optionId}
                                    type="checkbox"
                                    className="research-filter-dropdown__checkbox"
                                    checked={isChecked}
                                    onChange={() => handleToggle(option.value)}
                                    aria-label={getTranslation("research-page.filter-option-count", "{value} ({count})", {value: option.value, count: option.count})}
                                />
                                <span className="research-filter-dropdown__option-label">{option.value}</span>
                                <span className="research-filter-dropdown__option-count">{option.count}</span>
                            </label>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
